/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useProjectAuthStore, ProjectRoleType } from '@coze-common/auth';
import { IntelligenceType } from '@coze-arch/idl/intelligence_api';
import { intelligenceApi } from '@coze-arch/bot-api';

export function useInitProjectRole(spaceId: string, projectId: string) {
  const { setIsReady, setRoles, isReady } = useProjectAuthStore(
    useShallow(store => ({
      isReady: store.isReady[projectId],
      setIsReady: store.setIsReady,
      setRoles: store.setRoles,
    })),
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // 拉取失败时降级为无项目角色，避免授予过高权限
      let roles: ProjectRoleType[] = [];
      try {
        const res = await intelligenceApi.GetDraftIntelligenceInfo({
          intelligence_id: projectId,
          intelligence_type: IntelligenceType.Project,
        });
        if (res.data?.is_owner) {
          roles = [ProjectRoleType.Owner];
        }
      } catch {
        roles = [];
      }

      if (!cancelled) {
        setRoles(projectId, roles);
        setIsReady(projectId, true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return isReady; // Whether the initialization is complete.
}
